import React from 'react';
import { Link } from 'react-router-dom';

interface LessonTeachHeaderProps {
  isOpsSurface: boolean;
  isFocusViewport: boolean;
  lessonControlLayoutV2: boolean;
  workspaceModeEnabled: boolean;
  embedded?: boolean;
  advanceLabel?: string;
  isTestMode: boolean;
  trackAssignment: boolean;
  testModeSaving: boolean;
  trackLoading: boolean;
  showInlineOpsPanel: boolean;
  testModeError: string | null;
  trackError: string | null;
  copiedLink: boolean;
  currentStepId: number | null;
  lessonTitle?: string;
  sectionName?: string;
  sectionId?: number;
  currentStage?: string;
  currentStepLabel?: string;
  currentStepTitle?: string;
  pacingMode?: 'whole_class' | 'selective';
  targetedCount?: number;
  joinedCount?: number;
  sectionStudentCount?: number;
  runStatus?: 'active' | 'paused' | 'completed';
  explainActive?: boolean;
  explainLoading: boolean;
  hasPreviousStep: boolean;
  stepNavLoadingId: number | null;
  endRunSubmitting: boolean;
  studioAvailable?: boolean;
  canEnterStudio?: boolean;
  deliveryPhase?: string;
  studioActionLoading?: boolean;
  alertCount?: number;
  alertLabel?: string;
  responseProgressLabel?: string;
  materialsCount?: number;
  onToggleTestMode: (nextMode: boolean) => void;
  onToggleTrackAssignment: (nextValue: boolean) => void;
  onOpenRunSettings: () => void;
  onOpenStepJump: () => void;
  onOpenOpsInNewTab: () => void;
  onToggleInlineOpsPanel: () => void;
  onOpenOpsSameTab: () => void;
  onOpenMobileOpsDrawer: () => void;
  onOpenTeachSameTab: () => void;
  onOpenDisplay: () => void;
  onOpenStudentPreview: () => void;
  onCopyStudentLink: () => void;
  onOpenExplain: () => void;
  onClearExplain: () => void;
  onGoPreviousStep: () => void;
  onAdvanceStep: () => void;
  onEndRun: () => void;
  onEnterStudio?: () => void;
  onAlertClick?: () => void;
  onAlertPanelClick?: () => void;
  onOpenMaterials?: () => void;
}

export const LessonTeachHeader: React.FC<LessonTeachHeaderProps> = ({
  isOpsSurface,
  isFocusViewport,
  lessonControlLayoutV2,
  workspaceModeEnabled,
  embedded,
  advanceLabel,
  isTestMode,
  trackAssignment,
  testModeSaving,
  trackLoading,
  showInlineOpsPanel,
  testModeError,
  trackError,
  copiedLink,
  currentStepId,
  lessonTitle = 'Lesson',
  sectionName = 'Section',
  sectionId,
  currentStage = '',
  currentStepLabel,
  currentStepTitle,
  pacingMode = 'whole_class',
  targetedCount = 0,
  joinedCount = 0,
  sectionStudentCount = 0,
  runStatus = 'active',
  explainActive = false,
  explainLoading,
  hasPreviousStep,
  stepNavLoadingId,
  endRunSubmitting,
  studioAvailable = false,
  canEnterStudio = false,
  deliveryPhase = 'live',
  studioActionLoading = false,
  alertCount = 0,
  alertLabel = '',
  responseProgressLabel: rawResponseProgressLabel = '',
  materialsCount = 0,
  onToggleTestMode,
  onToggleTrackAssignment,
  onOpenRunSettings,
  onOpenStepJump,
  onOpenOpsInNewTab,
  onToggleInlineOpsPanel,
  onOpenOpsSameTab,
  onOpenMobileOpsDrawer,
  onOpenTeachSameTab,
  onOpenDisplay,
  onOpenStudentPreview,
  onCopyStudentLink,
  onOpenExplain,
  onClearExplain,
  onGoPreviousStep,
  onAdvanceStep,
  onEndRun,
  onEnterStudio,
  onAlertClick,
  onAlertPanelClick,
  onOpenMaterials,
}) => {
  const teachHref = `/v2/teacher/teach${sectionId ? `?section=${sectionId}` : ''}`;
  const responseProgressLabel = rawResponseProgressLabel
    ? `${rawResponseProgressLabel.split(' responded')[0]} responded`
    : '';
  if (embedded) return <header className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-surface p-4">
    <div><p className="text-sm text-muted">Teacher-led · {currentStepLabel}</p><h2 className="text-xl font-medium">{currentStepTitle}</h2></div>
    <div className="flex flex-wrap gap-2"><button className="btn-secondary" disabled={!hasPreviousStep || stepNavLoadingId !== null} onClick={onGoPreviousStep}>Previous step</button>{canEnterStudio && onEnterStudio ? <button className="btn-primary" disabled={studioActionLoading} onClick={onEnterStudio}>Open activity work time</button> : <button className="btn-primary" disabled={stepNavLoadingId !== null || endRunSubmitting} onClick={onAdvanceStep}>{stepNavLoadingId !== null ? 'Changing…' : advanceLabel || 'Next step'}</button>}</div>
    <details className="w-full"><summary className="cursor-pointer text-sm text-brand">Activity tools</summary><div className="mt-3 flex flex-wrap gap-2"><button className="btn-secondary" onClick={onOpenStepJump}>Choose step</button><button className="btn-secondary" onClick={onOpenDisplay}>Open Display</button><button className="btn-secondary" onClick={onOpenOpsInNewTab}>Student responses</button><button className="btn-secondary" onClick={onEndRun}>End run with attendance review</button></div></details>
  </header>;
  if (lessonControlLayoutV2 && workspaceModeEnabled && !isOpsSurface) {
    const studioActive = String(deliveryPhase || 'live').toLowerCase() === 'studio';
    const runActive = runStatus === 'active';
    const showStudioPrimary = runActive && studioAvailable && !studioActive && canEnterStudio;
    const showExplainPrimary = runActive && explainActive;
    const pacingLabel = pacingMode === 'selective' ? 'Selective instruction' : 'Whole Class';
    const stageAndStep = [currentStage ? currentStage.toUpperCase() : '', currentStepLabel].filter(Boolean).join(' · ');
    return (
      <div className={`${embedded ? "" : "sticky top-0"} z-30 shrink-0 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-[220px] flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {!embedded && <Link
                to={teachHref}
                className="rounded border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Back to Teach
              </Link>}
              <span className="truncate text-base font-semibold text-slate-950">{lessonTitle}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                runStatus === 'active'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-slate-100 text-slate-700'
              }`}>
                {runStatus === 'active' ? 'Live' : runStatus}
              </span>
              {runActive && alertCount > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                  <button
                    type="button"
                    className="font-medium hover:underline"
                    onClick={onAlertClick}
                  >
                    {alertLabel || `${alertCount} alert${alertCount === 1 ? '' : 's'}`}
                  </button>
                  {onAlertPanelClick ? (
                    <button
                      type="button"
                      className="rounded border border-amber-200 bg-white px-1.5 py-0.5 text-[11px] font-medium hover:bg-amber-50"
                      onClick={onAlertPanelClick}
                    >
                      Signals
                    </button>
                  ) : null}
                </span>
              ) : null}
            </div>
            <p className="mt-1 truncate text-xs text-slate-600">{sectionName}</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700">
              {stageAndStep || 'Waiting for the next step'}
            </p>
            <h1 className="mt-0.5 truncate text-lg font-semibold text-slate-950">
              {currentStepTitle || 'Waiting for the next step'}
            </h1>
            {runActive ? <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
              {studioActive ? (
                <>
                  <span>Live instruction: {pacingLabel} ({targetedCount} targeted)</span>
                  <span aria-hidden="true">·</span>
                  <span className="font-medium text-amber-800">Studio: Full section ({sectionStudentCount} students)</span>
                </>
              ) : (
                <>
                  <span>{pacingLabel}</span><span aria-hidden="true">·</span>
                  <span>{targetedCount} targeted</span>
                </>
              )}
              <span aria-hidden="true">·</span>
              <span>{joinedCount} on live page</span>
              {responseProgressLabel ? (
                <><span aria-hidden="true">·</span><button type="button" className="font-medium text-indigo-700 hover:underline" onClick={onAlertPanelClick || onAlertClick}>{responseProgressLabel}</button></>
              ) : null}
            </div> : <p className="mt-1 text-xs text-slate-600">{runStatus === 'completed' ? 'Completed session' : 'Paused session'}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="rounded border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50" onClick={onOpenDisplay}>
              Open Display
            </button>
            <button
              className="rounded border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={onGoPreviousStep}
              disabled={!hasPreviousStep || stepNavLoadingId !== null || !runActive}
            >
              Previous Step
            </button>
            {showExplainPrimary ? (
              <button type="button" data-testid="lesson-primary-action" className="rounded bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50" onClick={onClearExplain} disabled={explainLoading}>
                {explainLoading ? 'Clearing...' : 'Clear Explain'}
              </button>
            ) : showStudioPrimary ? (
              <button type="button" data-testid="lesson-primary-action" className="rounded bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50" onClick={onEnterStudio} disabled={studioActionLoading || !onEnterStudio}>
                {studioActionLoading ? 'Starting...' : 'Start Independent Work'}
              </button>
            ) : runStatus === 'active' ? (
              <button type="button" data-testid="lesson-primary-action" className="rounded bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50" onClick={onAdvanceStep} disabled={stepNavLoadingId !== null}>
                {stepNavLoadingId !== null ? 'Changing...' : 'Advance Step'}
              </button>
            ) : (
              <button type="button" data-testid="lesson-primary-action" className="rounded bg-slate-400 px-5 py-2.5 text-sm font-semibold text-white" disabled>
                {runStatus === 'completed' ? 'Run Ended' : 'Run Paused'}
              </button>
            )}
            {studioActive ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                {runActive ? 'Studio Active' : 'Independent work record'}
              </span>
            ) : null}
            <details className="relative">
              <summary className="list-none rounded border border-slate-200 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
                More
              </summary>
              <div className="absolute right-0 z-40 mt-2 w-72 rounded-lg border border-slate-200 bg-white p-2 text-sm shadow-lg">
                <div className="grid gap-1">
                  <button className="rounded px-3 py-2 text-left hover:bg-slate-50" onClick={onOpenOpsInNewTab}>Open Ops</button>
                  <button className="rounded px-3 py-2 text-left hover:bg-slate-50" onClick={onOpenStudentPreview}>Student Preview</button>
                  {materialsCount > 0 && onOpenMaterials ? (
                    <button className="rounded px-3 py-2 text-left hover:bg-slate-50" onClick={onOpenMaterials}>Lesson Materials ({materialsCount})</button>
                  ) : null}
                  <button className="rounded px-3 py-2 text-left hover:bg-slate-50" onClick={onOpenStepJump}>Jump to step</button>
                  <button className="rounded px-3 py-2 text-left hover:bg-slate-50" onClick={onCopyStudentLink}>{copiedLink ? 'Copied ✓' : 'Copy student link'}</button>
                  <button className="rounded px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50" onClick={onOpenExplain} disabled={!currentStepId || !runActive}>Explain to Class</button>
                  {!showExplainPrimary ? <button className="rounded px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50" onClick={onClearExplain} disabled={explainLoading}>Clear Explain</button> : null}
                  <button className="rounded px-3 py-2 text-left hover:bg-slate-50" onClick={onOpenRunSettings}>Run Settings</button>
                  <button className="rounded px-3 py-2 text-left text-rose-700 hover:bg-rose-50 disabled:opacity-50" onClick={onEndRun} disabled={endRunSubmitting}>End Run</button>
                </div>
                <div className="mt-2 border-t border-slate-100 pt-2">
                  <label className="flex items-center gap-2 px-3 py-1.5 text-slate-700">
                    <input type="checkbox" className="rounded" checked={isTestMode} disabled={testModeSaving} onChange={(event) => onToggleTestMode(event.target.checked)} />
                    Exclude from reports
                  </label>
                  <label className="flex items-center gap-2 px-3 py-1.5 text-slate-700">
                    <input type="checkbox" className="rounded" checked={trackAssignment} disabled={trackLoading} onChange={(event) => onToggleTrackAssignment(event.target.checked)} />
                    Track as make-up
                  </label>
                  {(testModeError || trackError) ? (
                    <div className="px-3 py-1 text-xs text-rose-700">{testModeError || trackError}</div>
                  ) : null}
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="shrink-0 flex items-center justify-between">
    <div>
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isOpsSurface ? 'Lesson Ops' : 'Lesson Control'}
        </h1>
        <span className={`ml-2 inline-flex items-center rounded-full border px-2 py-1 text-xs ${
          isTestMode
            ? 'border-amber-200 bg-amber-50 text-amber-700'
            : 'border-slate-200 bg-slate-50 text-slate-600'
        }`}>
          Test Mode: {isTestMode ? 'On' : 'Off'}
        </span>
        <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs ${
          trackAssignment
            ? 'border-indigo-100 bg-indigo-50 text-indigo-700'
            : 'border-slate-200 bg-slate-50 text-slate-600'
        }`}>
          Make-up: {trackAssignment ? 'On' : 'Off'}
        </span>
        {lessonControlLayoutV2 && isFocusViewport && !isOpsSurface && !workspaceModeEnabled && (
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-700">
            Teaching Focus
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500">{isOpsSurface ? 'Operations workspace' : 'Live classroom cockpit'}</p>
      {responseProgressLabel ? (
        <button type="button" className="mt-1 text-xs font-medium text-indigo-700 hover:underline" onClick={onAlertPanelClick || onAlertClick}>
          {responseProgressLabel}
        </button>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded"
            checked={isTestMode}
            disabled={testModeSaving}
            onChange={(event) => onToggleTestMode(event.target.checked)}
          />
          Exclude this run from reports (Test Mode)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded"
            checked={trackAssignment}
            disabled={trackLoading}
            onChange={(event) => onToggleTrackAssignment(event.target.checked)}
          />
          Track as make-up assignment
        </label>
        <button
          type="button"
          className={`rounded border border-gray-200 px-3 py-1.5 text-sm ${trackLoading || testModeSaving ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
          onClick={onOpenRunSettings}
          disabled={trackLoading || testModeSaving}
        >
          Run Settings
        </button>
        {lessonControlLayoutV2 && !isOpsSurface && !workspaceModeEnabled && (
          <>
            <button
              type="button"
              className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
              onClick={onOpenStepJump}
            >
              Jump to step
            </button>
            <button
              type="button"
              className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
              onClick={onOpenOpsInNewTab}
            >
              Open Ops
            </button>
            {!isFocusViewport && (
              <button
                type="button"
                className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                onClick={onToggleInlineOpsPanel}
              >
                {showInlineOpsPanel ? 'Hide Ops Panel' : 'Show Ops Panel'}
              </button>
            )}
            {isFocusViewport && (
              <button
                type="button"
                className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                onClick={onOpenOpsSameTab}
              >
                Ops (same tab)
              </button>
            )}
            {isFocusViewport && (
              <button
                type="button"
                className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                onClick={onOpenMobileOpsDrawer}
              >
                Ops Drawer
              </button>
            )}
          </>
        )}
        {lessonControlLayoutV2 && isOpsSurface && (
          <button
            type="button"
            className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
            onClick={onOpenTeachSameTab}
          >
            Back to Teach
          </button>
        )}
      </div>
      {testModeError && (
        <div className="mt-2 text-xs text-rose-700">{testModeError}</div>
      )}
      {trackError && (
        <div className="mt-2 text-xs text-rose-700">{trackError}</div>
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      {!isOpsSurface ? (
        <Link className="px-4 py-2 bg-white border border-gray-200 rounded" to={teachHref}>
          Back to Teach
        </Link>
      ) : null}
      <button className="px-4 py-2 bg-white border border-gray-200 rounded" onClick={onOpenDisplay}>
        Open Display
      </button>
      <button className="px-4 py-2 bg-sky-50 border border-sky-200 text-sky-800 rounded" onClick={onOpenStudentPreview}>
        Student Preview
      </button>
      {materialsCount > 0 && onOpenMaterials ? (
        <button className="px-4 py-2 bg-white border border-gray-200 rounded" onClick={onOpenMaterials}>
          Materials ({materialsCount})
        </button>
      ) : null}
      {lessonControlLayoutV2 && !isOpsSurface && (
        <button className="px-4 py-2 bg-white border border-gray-200 rounded" onClick={onOpenOpsInNewTab}>
          Open Ops
        </button>
      )}
      <button className="px-4 py-2 bg-white border border-gray-200 rounded" onClick={onCopyStudentLink}>
        {copiedLink ? 'Copied ✓' : 'Copy student link'}
      </button>
      <button
        className="px-4 py-2 bg-white border border-gray-200 rounded disabled:opacity-50"
        onClick={onOpenExplain}
        disabled={!currentStepId}
      >
        Explain to Class
      </button>
      <button
        className="px-4 py-2 bg-white border border-gray-200 rounded disabled:opacity-50"
        onClick={onClearExplain}
        disabled={explainLoading}
      >
        Clear Explain
      </button>
      <button
        className="px-4 py-2 bg-white border border-gray-200 rounded disabled:opacity-50"
        onClick={onGoPreviousStep}
        disabled={!hasPreviousStep || stepNavLoadingId !== null}
      >
        Previous Step
      </button>
      <button
        className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
        onClick={onAdvanceStep}
        disabled={stepNavLoadingId !== null}
      >
        {stepNavLoadingId !== null ? 'Changing step...' : 'Advance Step'}
      </button>
      <button
        className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        onClick={onEndRun}
        disabled={endRunSubmitting}
      >
        End Run
      </button>
    </div>
  </div>
  );
};
